import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
// Explicit extensions: this is a JS CRA project without tsconfig.json, so
// webpack only resolves .ts/.tsx imports when the extension is given.
import { amountInWords, resolveUomName } from './numberFormatter.ts';
import { THERMAL_PAGE_WIDTH_PT } from './ThermalPdfPage.ts';

const PAGE_HEIGHT_PT = 700;

// Thermal printer styles — page is printable width (72mm), not 80mm paper,
// so Chrome/Windows print preview fits at Actual size without blur.
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 5,
    paddingHorizontal: 8,
    paddingBottom: 8,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    minHeight: 800,
  },

  // Header section
  header: {
    alignItems: 'center',
    marginBottom: 0,
  },

  title: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 2,
    marginTop: 2,
  },

  companyName: {
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 2,
  },

  addressText: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 1,
  },

  phoneText: {
    fontSize: 8,
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: 1,
  },

  separator: {
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    marginVertical: 5,
  },

  // Invoice details section
  invoiceDetails: {
    marginBottom: 1,
  },

  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 0.5,
  },

  detailLabel: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  detailValue: {
    fontSize: 8,
  },

  remarksRow: {
    flexDirection: 'row',
    marginBottom: 0.5,
  },

  remarksLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    width: '25%',
  },

  remarksValue: {
    fontSize: 8,
    flex: 1,
    marginLeft: 5,
    textAlign: 'right',
  },

  // Items section
  itemsHeader: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    paddingVertical: 3,
    marginBottom: 2,
  },

  headerText: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },

  itemContainer: {
    marginBottom: 4,
    paddingBottom: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#CCCCCC',
  },

  itemName: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 1,
    flex: 1,
  },

  itemSrNo: {
    fontSize: 9,
    fontWeight: 'bold',
    width: 16,
    marginRight: 2,
  },

  itemNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 1,
  },

  itemDetailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  itemDetail: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  // Totals section
  totalsContainer: {
    marginTop: 2,
    paddingTop: 3,
    borderTopWidth: 1,
    borderTopColor: '#000000',
  },

  // Total Qty and Subtotal share one row: Qty gets 40%, Subtotal gets 60%.
  totalQtySubtotalRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },

  totalQtyCol: {
    width: '40%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingRight: 4,
    borderRightWidth: 0.75,
    borderRightColor: '#000000',
  },

  subtotalCol: {
    width: '60%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 6,
  },

  totalQtyValue: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  totalLabel: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  totalValue: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    marginTop: 2,
  },

  amountWordsRow: {
    marginTop: 4,
    flexDirection: 'row',
  },

  amountWordsValue: {
    fontSize: 8.5,
    fontFamily: 'Helvetica-Bold',
    fontWeight: 'bold',
    color: '#000000',
    lineHeight: 1.3,
    flex: 1,
  },

  // Tax section
  taxContainer: {
    marginTop: 6,
    marginBottom: 2,
    paddingTop: 6,
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
  },

  sectionTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 0.2,
    borderBottomColor: '#e0e0e0',
  },
  tableTotalRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderTopWidth: 1,
    borderTopColor: '#000000',
  },
  tableHeaderText: {
    fontWeight: 'bold',
    fontSize: 8,
    textAlign: 'center',
    color: '#000000',
  },
  tableCellText: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#000000',
  },

  // Column widths for tax breakdown
  indexColumn: {
    flex: 1,
  },
  taxableColumn: {
    flex: 1.6,
  },
  taxColumn: {
    flex: 1.2,
  },
  amountColumn: {
    flex: 1.5,
  },

  // Footer
  footer: {
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#000000',
  },

  thankYou: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 4,
  },
});

// Renders the page for a single bill; ThermalBillPDF wraps one or more of
// these in a Document so multiple selected bills print as one PDF.
const ThermalBillPage: React.FC<{ data: any }> = ({ data }) => {
  const subtotal = data?.totalAmt || 0;
  const finalTotal = data?.payableAmt || 0;

  const totalQty = (data?.items || []).reduce(
    (sum: number, item: any) => sum + (Number(item?.qty) || 0),
    0,
  );
  const totalAmountWords = amountInWords(finalTotal);

  // Skip all-zero tax rows so the section doesn't render an empty table.
  const meaningfulTaxBreakdown = (data?.taxBreakdown || []).filter(
    (tax: any) =>
      Number(tax?.taxableAmt || 0) > 0 ||
      Number(tax?.cgstAmt || 0) > 0 ||
      Number(tax?.sgstAmt || 0) > 0,
  );

  const taxBreakdownTotals = meaningfulTaxBreakdown.reduce(
    (acc: any, tax: any) => ({
      taxableAmt: acc.taxableAmt + (Number(tax?.taxableAmt) || 0),
      cgstAmt: acc.cgstAmt + (Number(tax?.cgstAmt) || 0),
      sgstAmt: acc.sgstAmt + (Number(tax?.sgstAmt) || 0),
    }),
    { taxableAmt: 0, cgstAmt: 0, sgstAmt: 0 },
  );

  return (
    <Page
      size={[THERMAL_PAGE_WIDTH_PT, PAGE_HEIGHT_PT]}
      style={styles.page}
      wrap={false}
    >
      {/* Company Header */}
      <View style={styles.header}>
        <Text style={styles.title}>RETAIL INVOICE</Text>

        {data.company.name && data.company.name !== 'NA' && (
          <Text style={styles.companyName}>{data.company.name}</Text>
        )}

        {data.company.address && data.company.address !== 'NA' && (
          <Text style={styles.addressText}>{data.company.address}</Text>
        )}

        {data.company.phone && data.company.phone !== 'NA' && (
          <Text style={styles.phoneText}>Outlet Ph: {data.company.phone}</Text>
        )}

        {data.company.fssai && data.company.fssai !== 'NA' && (
          <Text style={styles.addressText}>FSSAI: {data.company.fssai}</Text>
        )}
      </View>

      <View style={styles.separator} />

      {/* Invoice Details */}
      <View style={styles.invoiceDetails}>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Inv No:</Text>
          <Text style={styles.detailValue}>{data.invoiceNo}</Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Bill Date:</Text>
          <Text style={styles.detailValue}>{data.insertDateTime}</Text>
        </View>
        <View style={styles.detailRow}>
          <Text style={styles.detailLabel}>Customer:</Text>
          <Text style={styles.detailValue}>
            {data.customer.name} [{data.customer.phone || '000000000'}]
          </Text>
        </View>
        {data.company.gstin && data.company.gstin !== 'NA' && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Customer GST:</Text>
            <Text style={styles.detailValue}>{data.company.gstin}</Text>
          </View>
        )}
        {data?.employeeName && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Sales Person: </Text>
            <Text style={styles.detailValue}>{data.employeeName}</Text>
          </View>
        )}
        {data?.remark ? (
          <View style={styles.remarksRow}>
            <Text style={styles.remarksLabel}>Remark:</Text>
            <Text style={styles.remarksValue}>{data.remark}</Text>
          </View>
        ) : null}
      </View>

      <View style={styles.itemsHeader}>
        <Text style={[styles.headerText, { width: '5%' }]}>Sr</Text>
        <Text style={[styles.headerText, { width: '25%' }]}>Item</Text>
        <Text style={[styles.headerText, { width: '10%' }]}>UOM</Text>
        <Text style={[styles.headerText, { width: '15%' }]}>Qty</Text>
        <Text style={[styles.headerText, { width: '20%' }]}>Rate</Text>
        <Text style={[styles.headerText, { width: '25%' }]}>Amount</Text>
      </View>

      {data?.items?.map((item: any, index: number) => (
        <View style={styles.itemContainer} key={index}>
          <View style={styles.itemNameRow}>
            <Text style={styles.itemSrNo}>{index + 1}.</Text>
            <Text style={styles.itemName}>
              {item.name} (HSN - {item.hsnCode})
            </Text>
          </View>
          <View style={styles.itemDetailsRow}>
            <Text style={styles.itemDetail}>
              UOM : {resolveUomName(item.uomName, 'NA')}
            </Text>
            <Text style={styles.itemDetail}>Qty: {item.qty}</Text>
            <Text style={styles.itemDetail}>Rate: {item.rate.toFixed(2)}</Text>
            <Text style={styles.itemDetail}>
              Amt: {item.amount.toFixed(2)}
            </Text>
          </View>
        </View>
      ))}

      <View style={styles.totalsContainer}>
        <View style={styles.totalQtySubtotalRow}>
          <View style={styles.totalQtyCol}>
            <Text style={styles.totalLabel}>Total Qty:</Text>
            <Text style={styles.totalQtyValue}>{totalQty.toFixed(3)}</Text>
          </View>
          <View style={styles.subtotalCol}>
            <Text style={styles.totalLabel}>Subtotal:</Text>
            <Text style={styles.totalValue}>{subtotal.toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.grandTotalRow}>
          <Text style={styles.totalLabel}>Final Total:</Text>
          <Text style={styles.totalValue}>{finalTotal.toFixed(2)}</Text>
        </View>

        <View style={styles.amountWordsRow}>
          <Text style={styles.amountWordsValue}>{totalAmountWords}</Text>
        </View>
      </View>

      {/* Tax Breakdown */}
      {meaningfulTaxBreakdown.length > 0 && (
        <View style={styles.taxContainer}>
          <Text style={styles.sectionTitle}>TAX BREAKDOWN</Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.indexColumn]}>
              Index
            </Text>
            <Text style={[styles.tableHeaderText, styles.taxableColumn]}>
              Taxable Amt
            </Text>
            <Text style={[styles.tableHeaderText, styles.taxColumn]}>
              CGST %
            </Text>
            <Text style={[styles.tableHeaderText, styles.amountColumn]}>
              CGST Amt
            </Text>
            <Text style={[styles.tableHeaderText, styles.taxColumn]}>
              SGST %
            </Text>
            <Text style={[styles.tableHeaderText, styles.amountColumn]}>
              SGST Amt
            </Text>
          </View>
          {meaningfulTaxBreakdown.map((tax: any, index: number) => (
            <View style={styles.tableRow} key={index}>
              <Text style={[styles.tableCellText, styles.indexColumn]}>
                {tax.index}
              </Text>
              <Text style={[styles.tableCellText, styles.taxableColumn]}>
                {(tax.taxableAmt || 0).toFixed(2)}
              </Text>
              <Text style={[styles.tableCellText, styles.taxColumn]}>
                {tax.cgstPer}%
              </Text>
              <Text style={[styles.tableCellText, styles.amountColumn]}>
                {(tax.cgstAmt || 0).toFixed(2)}
              </Text>
              <Text style={[styles.tableCellText, styles.taxColumn]}>
                {tax.sgstPer}%
              </Text>
              <Text style={[styles.tableCellText, styles.amountColumn]}>
                {(tax.sgstAmt || 0).toFixed(2)}
              </Text>
            </View>
          ))}
          <View style={styles.tableTotalRow}>
            <Text style={[styles.tableCellText, styles.indexColumn]}>
              Total
            </Text>
            <Text style={[styles.tableCellText, styles.taxableColumn]}>
              {taxBreakdownTotals.taxableAmt.toFixed(2)}
            </Text>
            <Text style={[styles.tableCellText, styles.taxColumn]}></Text>
            <Text style={[styles.tableCellText, styles.amountColumn]}>
              {taxBreakdownTotals.cgstAmt.toFixed(2)}
            </Text>
            <Text style={[styles.tableCellText, styles.taxColumn]}></Text>
            <Text style={[styles.tableCellText, styles.amountColumn]}>
              {taxBreakdownTotals.sgstAmt.toFixed(2)}
            </Text>
          </View>
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.thankYou}>Thank You. Please Visit Again!</Text>
      </View>
    </Page>
  );
};

// `data` can be a single bill or an array of bills.
const ThermalBillPDF: React.FC<{ data: any }> = ({ data }) => {
  const bills = Array.isArray(data) ? data : [data];
  return (
    <Document>
      {bills.map((bill, billIndex) => (
        <ThermalBillPage key={`bill-${billIndex}`} data={bill} />
      ))}
    </Document>
  );
};

export default ThermalBillPDF;
